import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from '@untitledui/icons';

type ExitConfirmModalProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

// Modal de confirmacao para sair do cadastro. Estilo Apple:
//  - backdrop blur leve sobre fundo escurecido
//  - card branco com border-radius generoso (24px = rounded-3xl)
//  - X discreto no canto superior direito
//  - animacao de fade + scale para entrada/saida
//  - bloqueia scroll do body enquanto aberto
//  - fecha em ESC e clique no backdrop
//  - botoes "Continuar cadastro" (primario) e "Sair" (destrutivo)
export const ExitConfirmModal = ({ open, onClose, onConfirm }: ExitConfirmModalProps) => {
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  // Mount/unmount com delay para a animacao de saida rodar antes do desmonte.
  useEffect(() => {
    if (open) {
      setMounted(true);
      // Forca um reflow no proximo frame para que `visible=true` dispare a transicao.
      requestAnimationFrame(() => setVisible(true));
    } else if (mounted) {
      setVisible(false);
      const timer = window.setTimeout(() => setMounted(false), 200);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [open, mounted]);

  // Trava scroll do body quando aberto.
  useEffect(() => {
    if (!open) return undefined;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, [open]);

  // Fechar com ESC.
  useEffect(() => {
    if (!open) return undefined;
    const handle = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handle);
    return () => window.removeEventListener('keydown', handle);
  }, [open, onClose]);

  if (!mounted) return null;

  return createPortal(
    <div
      role="presentation"
      onClick={onClose}
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
    >
      {/* Backdrop com blur estilo Apple */}
      <div
        aria-hidden="true"
        className={`absolute inset-0 bg-black/40 backdrop-blur-md transition-opacity duration-200 ease-out ${
          visible ? 'opacity-100' : 'opacity-0'
        }`}
      />
      {/* Card */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="exit-modal-title"
        aria-describedby="exit-modal-desc"
        onClick={(e) => e.stopPropagation()}
        className={`relative w-full max-w-[400px] rounded-3xl bg-white p-6 shadow-[0_20px_60px_-12px_rgba(16,24,40,0.25)] transition-all duration-200 ease-out sm:p-7 ${
          visible ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Fechar"
          className="absolute right-4 top-4 inline-flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2"
        >
          <X className="h-[18px] w-[18px]" strokeWidth={2.2} />
        </button>

        <div className="pr-6">
          <h2
            id="exit-modal-title"
            className="text-xl font-semibold tracking-tight text-gray-900"
          >
            Sair do cadastro?
          </h2>
          <p id="exit-modal-desc" className="mt-2 text-[15px] leading-relaxed text-gray-500">
            Você vai perder o progresso desta etapa. Tem certeza que deseja encerrar agora?
          </p>
        </div>

        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-3">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-11 items-center justify-center rounded-xl border border-gray-200 bg-white px-5 text-sm font-semibold text-gray-900 transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2"
          >
            Continuar cadastro
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="inline-flex h-11 items-center justify-center rounded-xl bg-gray-900 px-5 text-sm font-semibold text-white transition-colors hover:bg-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2"
          >
            Sair
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};
